import { ToEntity } from 'core/common/mappers';
import { formatPrice } from 'core/common/utils/formatPrice';
import { LocalizationService } from 'core/localization/services';
import { convertCurrencyToSymbol, CurrencySymbol } from 'core/payments/entities';
import {
  ComposedOffer,
  ContentOffer,
  Offer,
  OfferContentBase,
  OffersMap,
  OfferWithContentBase,
  StrictOffersMapByCategory,
} from '../entities';
import { OfferDto } from '../services/dto';

export class OfferMapper implements ToEntity<Offer, OfferDto> {
  private localizationService: LocalizationService;

  constructor() {
    this.localizationService = new LocalizationService();
    this.toEntity = this.toEntity.bind(this);
  }

  toEntity(dto: OfferDto): Offer {
    const currencySymbol = convertCurrencyToSymbol(dto.currency);
    const priceWithCurrency = this.getPriceWithCurrency(dto.price, currencySymbol);
    const trialPriceWithCurrency = this.getPriceWithCurrency(dto.trialPrice, currencySymbol);

    return {
      id: dto.id,
      offerId: dto.name,
      period: dto.period,
      trialPeriod: dto.trialPeriod,
      currency: dto.currency,
      currencySymbol,
      price: dto.price,
      pricePerDay: dto.pricePerDay,
      priceWithCurrency,
      trial: dto.trial,
      trialPrice: dto.trialPrice,
      trialPricePerDay: dto.trialPricePerDay,
      trialPriceWithCurrency,
    };
  }

  mapContentOffersToOffers(offers: StrictOffersMapByCategory, contentOffers?: ContentOffer) {
    if (!contentOffers) {
      return null;
    }

    return Object.entries(contentOffers).reduce<Record<string, ComposedOffer>>(
      (acc, [key, upsell]) => {
        const offerData = offers[key as keyof OffersMap];

        if (offerData && !Array.isArray(offerData)) {
          const oldPriceWithCurrency = this.getPriceWithCurrency(
            upsell.oldPrice,
            offerData.currencySymbol,
          );

          acc[key] = {
            ...this.mergeOffers(offerData, upsell),
            oldPriceWithCurrency,
          };
        }

        return acc;
      },
      {},
    );
  }

  matchOffersWithContent<TContent extends OfferContentBase>(
    offers: Array<Offer>,
    contentOffers?: Array<TContent>,
  ) {
    if (!contentOffers) {
      return offers as Array<OfferWithContentBase<TContent>>;
    }

    const sortedOffers = [...offers].sort((a, b) => {
      const aIdx = contentOffers.findIndex((item) => item.offerIdsToMatch.includes(a.offerId));
      const bIdx = contentOffers.findIndex((item) => item.offerIdsToMatch.includes(b.offerId));

      const aIdxSafe = aIdx === undefined || aIdx === -1 ? 1 : aIdx;
      const bIdxSafe = bIdx === undefined || bIdx === -1 ? 1 : bIdx;

      return aIdxSafe - bIdxSafe;
    });

    return sortedOffers.map((offer) => {
      const content = contentOffers.find((item) => item.offerIdsToMatch.includes(offer.offerId));

      if (!content) {
        return offer as OfferWithContentBase<TContent>;
      }

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { offerIdsToMatch, ...contentWithoutIds } = content;

      return {
        ...offer,
        ...contentWithoutIds,
      };
    });
  }

  private getPriceWithCurrency(price: number, currencySymbol: CurrencySymbol) {
    return this.localizationService.isRtl
      ? `${formatPrice(price)}${currencySymbol}`
      : `${currencySymbol}${formatPrice(price)}`;
  }

  private mergeOffers(baseOffer: Offer, contentOffer: ContentOffer) {
    return {
      ...baseOffer,
      ...contentOffer,
    };
  }
}
